import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function CompleteProfile() {
  usePageMeta({ title: "One last step | Solis Casting", noindex: true });
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("model");
  const [company, setCompany] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user === null) return <Navigate to="/auth" replace />;
  if (user?.role) return <Navigate to="/dashboard" replace />;

  const submit = async () => {
    if (!ageConfirmed) { toast.error("Please confirm you are 18 or older first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/complete-profile", { role, company: company || undefined, age_confirmed: ageConfirmed });
      setUser(data);
      toast.success(role === "model" ? "Welcome to the talent board." : "Welcome — your first casting awaits.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center px-6 sm:px-10" data-testid="complete-profile-page">
      <div className="max-w-xl mx-auto w-full">
        <p className="overline-label mb-6">One last step{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
        <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter mb-12">
          Choose your <span className="italic text-accent">side of the room.</span>
        </h1>
        <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-10" data-testid="complete-role-select">
          {[
            { v: "model", title: "I'm a model", sub: "Build your book, apply to casting calls" },
            { v: "employer", title: "I'm a brand", sub: "Post casting calls, review faces" },
          ].map((r) => (
            <button
              key={r.v}
              type="button"
              data-testid={`complete-role-${r.v}`}
              onClick={() => setRole(r.v)}
              className={`p-8 text-left transition-colors duration-300 ${
                role === r.v ? "bg-accent text-accent-foreground" : "bg-background hover:bg-white/5"
              }`}
            >
              <p className="font-display text-2xl mb-2">{r.title}</p>
              <p className={`font-grotesk text-[10px] uppercase tracking-[0.15em] ${role === r.v ? "text-white/80" : "text-white/40"}`}>{r.sub}</p>
            </button>
          ))}
        </div>
        {role === "employer" && (
          <input
            data-testid="complete-company-input"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="House / brand / agency"
            className="field mb-10"
          />
        )}
        <label data-testid="complete-age-consent" className="flex items-start gap-3 mb-8 cursor-pointer group">
          <input
            data-testid="complete-age-checkbox"
            type="checkbox"
            checked={ageConfirmed}
            onChange={(e) => setAgeConfirmed(e.target.checked)}
            className="mt-0.5 accent-[#C8102E]"
          />
          <span className="font-grotesk text-[11px] uppercase tracking-[0.12em] text-white/60 group-hover:text-white/90 transition-colors leading-relaxed">
            I confirm I am 18 or older <span className="text-accent">*</span>
          </span>
        </label>
        <button data-testid="complete-profile-submit" onClick={submit} disabled={busy} className="btn-primary w-full disabled:opacity-40">
          {busy ? "One moment…" : "Enter the atelier"}
        </button>
      </div>
    </div>
  );
}
